import React, { useEffect, useState } from "react";
import {
  createRazorPayOrder,
  publicPlayerPaymentHistory,
  razorPayOrderVerify,
} from "../../../services/publicService";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

const PublicPlayerPayment = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const getApiMessage = (error, fallbackMessage) => {
    return (
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      fallbackMessage
    );
  };

  const fetchData = async () => {
    if (!id) {
      setIsFetching(false);
      return;
    }

    setIsFetching(true);
    setFetchError("");
    try {
      const res = await publicPlayerPaymentHistory(id);
      setData(res);
    } catch (error) {
      console.error("Failed to fetch payment details:", error);
      const errorMessage = getApiMessage(
        error,
        "Unable to load payment details.",
      );
      setData(null);
      setFetchError(errorMessage);
      Swal.fire("Error!", errorMessage, "error");
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handlePayment = async () => {
    if (loading) return;

    if (!id) {
      Swal.fire("Error!", "Player ID is missing.", "error");
      return;
    }

    if (!data) {
      Swal.fire("Error!", "Payment details are not loaded yet.", "error");
      return;
    }

    if (!window.Razorpay) {
      Swal.fire(
        "Error!",
        "Payment gateway is not available right now. Please refresh and try again.",
        "error",
      );
      return;
    }

    const payload = {
      playerId: id,
    };

    setLoading(true);

    try {
      const orderRes = await createRazorPayOrder(payload);

      if (!orderRes) {
        throw new Error("Unable to create payment order.");
      }

      const options = {
        key: orderRes.keyId,
        amount: orderRes.amount,
        currency: "INR",
        name: "CrickPay",
        description: data?.packageName,
        order_id: orderRes.orderId,

        handler: async function (response) {
          try {
            const verifyPayload = {
              playerId: id,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            };

            const verifyRes = await razorPayOrderVerify(verifyPayload);

            if (verifyRes) {
              const successMessage =
                verifyRes?.message ||
                "Payment successful. Your subscription has been renewed.";
              Swal.fire("Success!", successMessage, "success");
              fetchData();
            } else {
              Swal.fire("Error!", "Payment verification failed.", "error");
            }
          } catch (error) {
            Swal.fire(
              "Error!",
              getApiMessage(error, "Payment verification failed."),
              "error",
            );
          } finally {
            setLoading(false);
          }
        },

        theme: {
          color: "#6673fd",
        },

        modal: {
          ondismiss() {
            setLoading(false);
            Swal.fire("Cancelled", "Payment cancelled.", "info");
          },
        },
      };

      const razor = new window.Razorpay(options);

      razor.on("payment.failed", () => {
        setLoading(false);
        Swal.fire("Error!", "Payment failed.", "error");
      });

      razor.open();
    } catch (error) {
      setLoading(false);
      Swal.fire(
        "Error!",
        getApiMessage(error, "Something went wrong."),
        "error",
      );
    }
  };

  const totalPayable = data?.isDiscount
    ? data?.totalPayable
    : data?.packageAmount;

  return (
    <div style={{ background: "#f8f9fb", minHeight: "100vh" }}>
      {/* Details */}

      <div className="container py-5 mt-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {isFetching && (
              <h3 className="fw-bold mb-4">Subscription Details</h3>
            )}
            {isFetching && (
              <div className="alert alert-info" role="alert">
                Loading payment details...
              </div>
            )}

            {!isFetching && fetchError && (
              <div className="alert alert-danger" role="alert">
                {fetchError}
              </div>
            )}

            {data && (
              <>
                <table className="table table-borderless bg-white shadow-sm rounded">
                  <tbody>
                    <tr className="border-bottom">
                      <th width="40%">Academy</th>
                      <td>{data?.tenantName}</td>
                    </tr>
                    <tr className="border-bottom">
                      <th width="40%">Academy Address</th>
                      <td>{data?.tenantAddress}</td>
                    </tr>
                    <tr className="border-bottom">
                      <th width="40%">Player Name</th>
                      <td>{data?.playerName}</td>
                    </tr>

                    <tr className="border-bottom">
                      <th>Player ID</th>
                      <td>{data?.playerId}</td>
                    </tr>

                    <tr className="border-bottom">
                      <th>Package</th>
                      <td>{data?.packageName}</td>
                    </tr>

                    <tr className="border-bottom">
                      <th>Duration</th>
                      <td>{data?.duration}</td>
                    </tr>

                    <tr className="border-bottom">
                      <th>Package Amount</th>
                      <td>₹ {data?.packageAmount}</td>
                    </tr>

                    {data?.isDiscount && (
                      <tr className="border-bottom">
                        <th>Discount</th>
                        <td className="text-success">
                          ₹ {data?.discountAmount}
                        </td>
                      </tr>
                    )}

                    <tr className="border-bottom">
                      <th>Total Payable</th>
                      <td className="fw-bold fs-4 text-primary">
                        ₹ {totalPayable}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-4">
                  <button
                    className="btn btn-primary btn-lg w-100"
                    style={{
                      height: 55,
                      borderRadius: 10,
                    }}
                    onClick={handlePayment}
                    disabled={loading || isFetching || !data}
                  >
                    {loading ? "Processing Payment..." : "Renew Subscription"}
                  </button>
                </div>
              </>
            )}

            {isFetching && (
              <p className="text-center text-muted mt-3">
                🔒 Secure payment powered by Razorpay
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicPlayerPayment;
